import { Link } from 'react-router-dom';
import { ArrowRight, Upload, Sparkles, ClipboardCheck, BarChart3, ShieldCheck, Zap, FileCheck2 } from 'lucide-react';

export default function Landing() {
  return (
    <>
      {/* HERO */}
      <section className="gradient-hero">
        <div className="container-wide py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <span className="chip bg-brand-50 text-brand-700 border border-brand-100">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> AI-powered grading
            </span>
            <h1 className="mt-4 text-4xl md:text-6xl font-display font-bold leading-tight text-ink-900">
              Grade any report in seconds.
              <span className="block bg-gradient-to-r from-brand-600 to-fuchsia-500 bg-clip-text text-transparent">
                Improve it to an A.
              </span>
            </h1>
            <p className="mt-5 text-lg text-ink-600 max-w-xl">
              Upload a Word or PDF report and get section-by-section feedback, a rubric-aligned score, and a concrete plan to make it better. Generate new reports from a topic in minutes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" className="btn-primary btn-lg">
                Start for free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="btn-secondary btn-lg">See pricing</Link>
            </div>
            <div className="mt-6 flex items-center gap-6 text-sm text-ink-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Privacy-first</span>
              <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-amber-500" /> Grading in ~20s</span>
              <span className="flex items-center gap-1.5"><FileCheck2 className="w-4 h-4 text-brand-600" /> DOCX & PDF</span>
            </div>
          </div>

          {/* Hero card mockup */}
          <div className="relative">
            <div className="card p-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-ink-500">Final_Thesis.docx</div>
                  <div className="font-semibold text-ink-900">Overall grade</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-display font-bold text-brand-700">87</div>
                  <div className="text-sm text-emerald-600 font-medium">A-</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {[
                  { k: 'Structure', v: 92 },
                  { k: 'Clarity', v: 85 },
                  { k: 'Evidence', v: 74 },
                  { k: 'Mechanics', v: 90 },
                ].map((s) => (
                  <div key={s.k} className="rounded-xl border border-ink-100 p-3">
                    <div className="flex items-center justify-between text-sm text-ink-600">
                      <span>{s.k}</span><span className="font-semibold text-ink-900">{s.v}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-brand-500 to-fuchsia-500" style={{ width: `${s.v}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800">
                Add two more primary sources in the Methodology section to lift Evidence above 85.
              </div>
            </div>
            <div className="hidden md:block absolute -z-10 inset-0 blur-3xl opacity-40 bg-gradient-to-br from-brand-400 via-fuchsia-400 to-emerald-300 rounded-[3rem]" />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20">
        <div className="container-wide">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold">Everything you need to write better reports</h2>
            <p className="mt-3 text-ink-600">Grade, generate, and improve — all in one place.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Feature icon={<Upload className="w-5 h-5" />} title="Upload & grade"
              body="Drop any .docx or .pdf and get a full breakdown across structure, clarity, depth, evidence, originality and mechanics." />
            <Feature icon={<ClipboardCheck className="w-5 h-5" />} title="Grade by your rubric"
              body="Paste or upload any rubric — we weight each criterion and produce a criterion-level score with supporting quotes." />
            <Feature icon={<Sparkles className="w-5 h-5" />} title="Generate from a topic"
              body="Give a topic, audience and target length. Get a structured, well-written report in any tone." />
            <Feature icon={<BarChart3 className="w-5 h-5" />} title="Actionable feedback"
              body="Every score comes with the precise sentences that hurt it — and a rewrite suggestion you can apply." />
            <Feature icon={<Zap className="w-5 h-5" />} title="Improve to A-grade"
              body="Pro tier rewrites weak sections to meet the rubric and returns a polished .docx. No guesswork." />
            <Feature icon={<ShieldCheck className="w-5 h-5" />} title="Private by default"
              body="Reports are processed in-memory and discarded after grading unless you choose to save them." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container-narrow card p-8 md:p-12 text-center bg-gradient-to-br from-brand-600 to-fuchsia-600 text-white border-0">
          <h3 className="text-2xl md:text-3xl font-display font-bold">Ready to grade your first report?</h3>
          <p className="mt-2 text-brand-100">Sign in — your first evaluation is on us.</p>
          <Link to="/login" className="btn-lg bg-white text-brand-700 mt-6 inline-flex hover:bg-brand-50">
            Start free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="card-hover p-6">
      <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 grid place-items-center">{icon}</div>
      <h3 className="mt-4 font-semibold text-ink-900">{title}</h3>
      <p className="mt-2 text-sm text-ink-600 leading-relaxed">{body}</p>
    </div>
  );
}
